// Runs the assistant's tool calls against the live app.
//
// Every handler goes through the same AppContext functions the UI uses, so
// streaks, habit-goal links and Google Calendar sync stay correct. AppContext
// callbacks close over the state from the last render, so a handler reads the
// app through get() and calls settle() after each write before the next one;
// otherwise a second write would start from stale lists and undo the first.
//
// Tool schemas live in functions/agentTools.js. Keep names in sync.

import { cleanName } from './assistantName';
import { computeProductivityScore } from '../utils/productivityScore';

const pad = (n) => String(n).padStart(2, '0');

export const localDateString = (date = new Date()) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const shiftDate = (dateString, days) => {
  const [y, m, d] = dateString.split('-').map(Number);
  return localDateString(new Date(y, m - 1, d + days));
};

const isDate = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

class ToolError extends Error {}
const fail = (message) => { throw new ToolError(message); };

const findOr = (list, id, label) => list.find((x) => x.id === id) || fail(`No ${label} with id ${id}. Call get_overview for current IDs.`);

// Only keep keys that were actually passed, so updates change only those fields
const pick = (pairs) => Object.fromEntries(pairs.filter(([, v]) => v !== undefined));

const eventDate = (e) => e.date || (e.startDate || e.start || '').split('T')[0] || null;

const calendarFor = (app, calendarId) => app.calendars.find((c) => c.id === calendarId);

// ---------- Read ----------

// The productivity score as the user sees it on Today, and what it's made of,
// so the assistant can say what would raise it (see "Score" in its prompt)
const scoreFor = (app) => {
  const s = computeProductivityScore({
    habits: app.habits,
    completions: app.completions,
    tasks: app.tasks,
    archivedTasks: app.archivedTasks,
    goals: app.goals,
    overdueCount: app.stats?.overdueCount || 0,
  });
  return {
    value: s.score,
    habits: { score: s.habitScore, weight_percent: s.weights.habits },
    tasks: { score: s.taskScore, weight_percent: s.weights.tasks, overdue: app.stats?.overdueCount || 0 },
    goals: { score: s.goalScore, weight_percent: s.weights.goals },
  };
};

const overview = ({ get }) => {
  const app = get();
  const today = localDateString();
  const week = Array.from({ length: 7 }, (_, i) => shiftDate(today, i - 6));
  const horizon = shiftDate(today, 14);
  const inWindow = (e) => { const d = eventDate(e); return d && d >= today && d <= horizon; };

  const syncedIds = new Set(app.events.filter((e) => e.googleEventId).map((e) => e.googleEventId));

  return {
    today,
    summary: {
      habits_done_today: `${app.stats.completedHabitsToday}/${app.stats.totalHabits}`,
      all_habits_streak_days: app.stats.streak,
      open_tasks: app.stats.activeTaskCount,
      overdue_tasks: app.stats.overdueCount,
      active_goals: app.stats.activeGoals,
    },
    score: scoreFor(app),
    habits: app.habits.map((h) => pick([
      ['id', h.id],
      ['name', h.name],
      ['time_of_day', h.timeOfDay],
      ['done_today', !!app.completions[h.id]?.[today]],
      ['last_7_days', week.map((d) => (app.completions[h.id]?.[d] ? '1' : '0')).join('')],
      ['current_streak', h.currentStreak || 0],
      ['longest_streak', h.longestStreak || 0],
      ['target_days', h.goal],
      ['why', h.why || undefined],
      ['linked_goal_ids', h.linkedGoalIds?.length ? h.linkedGoalIds : undefined],
    ])),
    tasks: app.tasks.map((t) => pick([
      ['id', t.id],
      ['title', t.title],
      ['done', !!t.completed],
      ['due_date', t.dueDate || undefined],
      ['priority', t.weight || t.priority],
      ['category', t.category],
      ['description', t.description || undefined],
    ])),
    goals: app.goals.map((g) => pick([
      ['id', g.id],
      ['title', g.title],
      ['done', !!g.completed],
      ['target_date', g.targetDate || undefined],
      ['description', g.description || g.specific || undefined],
      ['why', g.why || g.relevant || undefined],
      ['milestones', (g.milestones || []).map((m) => ({ id: m.id, title: m.title, done: !!m.completed }))],
      ['linked_habit_ids', g.linkedHabitIds?.length ? g.linkedHabitIds : undefined],
    ])),
    events: [
      ...app.events.filter(inWindow).map((e) => pick([
        ['id', e.id],
        ['name', e.name],
        ['date', eventDate(e)],
        ['end_date', e.endDate && e.endDate !== eventDate(e) ? e.endDate : undefined],
        ['start_time', e.startTime || undefined],
        ['end_time', e.endTime || undefined],
        ['location', e.location || undefined],
        ['calendar_id', e.calendarId],
      ])),
      // Read-only: events that live only in Google Calendar
      ...(app.googleCalendarEvents || [])
        .filter((e) => !syncedIds.has(e.id) && inWindow(e))
        .map((e) => pick([
          ['name', e.name],
          ['date', eventDate(e)],
          ['start_time', e.startTime || undefined],
          ['end_time', e.endTime || undefined],
          ['source', 'google'],
          ['read_only', true],
        ])),
    ].sort((a, b) => (a.date + (a.start_time || '')).localeCompare(b.date + (b.start_time || ''))),
    calendars: app.calendars.map((c) => pick([
      ['id', c.id],
      ['name', c.name],
      ['syncs_to_google', c.syncToGoogle ? true : undefined],
    ])),
    profile: app.agentProfile || {},
  };
};

const habitHistory = ({ get }, { habit_id, days = 30 }) => {
  const app = get();
  const habit = findOr(app.habits, habit_id, 'habit');
  const today = localDateString();
  const from = shiftDate(today, -(Math.min(Math.max(days, 1), 365) - 1));
  const done = Object.entries(app.completions[habit.id] || {})
    .filter(([d, v]) => v && d >= from && d <= today)
    .map(([d]) => d)
    .sort();
  return { habit: habit.name, from, to: today, days_done: done.length, dates_done: done };
};

const archive = ({ get }, { kind }) => {
  const app = get();
  if (kind === 'tasks') {
    return app.archivedTasks.map((t) => pick([['id', t.id], ['title', t.title], ['completed_at', t.completedAt]]));
  }
  return app.archivedGoals.map((g) => pick([['id', g.id], ['title', g.title], ['completed_at', g.completedAt]]));
};

// ---------- Habits ----------

const addHabits = async ({ get, settle }, { habits }) => {
  const ids = await get().addHabits(habits.map((h) => ({
    name: h.name,
    timeOfDay: h.time_of_day || 'anytime',
    goal: h.target_days || 30,
    linkedGoalIds: h.linked_goal_ids || [],
  })));
  await settle();
  // addHabits keeps a fixed set of fields; reasons go on afterwards
  for (let i = 0; i < habits.length; i++) {
    if (habits[i].why) {
      await get().updateHabit(ids[i], { why: habits[i].why });
      await settle();
    }
  }
  return {
    result: { added: habits.map((h, i) => ({ id: ids[i], name: h.name })) },
    summary: habits.length === 1 ? `Added habit: ${habits[0].name}` : `Added ${habits.length} habits`,
  };
};

const updateHabit = async ({ get }, { habit_id, name, time_of_day, target_days, why, linked_goal_ids }) => {
  const habit = findOr(get().habits, habit_id, 'habit');
  const updates = pick([['name', name], ['timeOfDay', time_of_day], ['goal', target_days], ['why', why], ['linkedGoalIds', linked_goal_ids]]);
  await get().updateHabit(habit.id, updates);
  return { result: { updated: habit.id }, summary: `Updated habit: ${name || habit.name}` };
};

const setHabitDone = async ({ get }, { habit_id, done, date }) => {
  const app = get();
  const habit = findOr(app.habits, habit_id, 'habit');
  const day = date || localDateString();
  if (!isDate(day)) fail('date must be YYYY-MM-DD');
  if (day > localDateString()) fail("Can't mark a future day.");
  const isDone = !!app.completions[habit.id]?.[day];
  if (isDone !== done) await app.toggleCompletion(habit.id, day);
  const when = day === localDateString() ? 'today' : day;
  return {
    result: { habit: habit.name, date: day, done },
    summary: `${habit.name}: ${done ? 'done' : 'not done'} ${when}`,
    // Consecutive calls for the same habit and state show as one line
    group: { key: `habit:${habit.id}:${done}`, label: `${habit.name}: ${done ? 'done' : 'not done'}`, date: day },
  };
};

const reorderHabits = async ({ get }, { habit_ids }) => {
  const app = get();
  if (habit_ids.length !== app.habits.length || !app.habits.every((h) => habit_ids.includes(h.id))) {
    fail('Pass every habit ID exactly once.');
  }
  await app.reorderHabits(habit_ids.map((id) => app.habits.find((h) => h.id === id)));
  return { result: { ok: true }, summary: 'Reordered habits' };
};

const deleteHabit = async ({ get }, { habit_id }) => {
  const habit = findOr(get().habits, habit_id, 'habit');
  await get().deleteHabit(habit.id);
  return { result: { deleted: habit.name }, summary: `Deleted habit: ${habit.name}` };
};

// ---------- Tasks ----------

const addTasks = async ({ get, settle }, { tasks }) => {
  const added = [];
  for (const t of tasks) {
    const before = new Set(get().tasks.map((x) => x.id));
    await get().addTask({
      title: t.title,
      description: t.description || '',
      dueDate: t.due_date || '',
      category: t.category || 'personal',
      weight: t.priority || 'medium',
    });
    await settle();
    const created = get().tasks.find((x) => !before.has(x.id));
    added.push({ id: created?.id, title: t.title });
  }
  return {
    result: { added },
    summary: tasks.length === 1 ? `Added task: ${tasks[0].title}` : `Added ${tasks.length} tasks`,
  };
};

const updateTask = async ({ get }, { task_id, title, description, due_date, category, priority }) => {
  const task = findOr(get().tasks, task_id, 'open task');
  if (due_date && !isDate(due_date)) fail('due_date must be YYYY-MM-DD or empty');
  await get().updateTask(task.id, pick([
    ['title', title], ['description', description], ['dueDate', due_date], ['category', category], ['weight', priority],
  ]));
  return { result: { updated: task.id }, summary: `Updated task: ${title || task.title}` };
};

const setTaskDone = async ({ get }, { task_id, done }) => {
  const task = findOr(get().tasks, task_id, 'open task');
  if (!!task.completed !== done) await get().toggleTask(task.id);
  return { result: { task: task.title, done }, summary: `${task.title}: ${done ? 'done' : 'reopened'}` };
};

const archiveTask = async ({ get }, { task_id }) => {
  const task = findOr(get().tasks, task_id, 'open task');
  await get().archiveTask(task.id);
  return { result: { archived: task.title }, summary: `Archived task: ${task.title}` };
};

const restoreTask = async ({ get }, { task_id, due_date }) => {
  const task = findOr(get().archivedTasks, task_id, 'archived task');
  await get().restoreTask(task.id, due_date);
  return { result: { restored: task.title }, summary: `Restored task: ${task.title}` };
};

const deleteTask = async ({ get }, { task_id }) => {
  const task = findOr(get().tasks, task_id, 'open task');
  await get().deleteTask(task.id);
  return { result: { deleted: task.title }, summary: `Deleted task: ${task.title}` };
};

// ---------- Goals ----------

const addGoal = async ({ get, settle }, { title, description, target_date, why, milestones = [], linked_habit_ids = [] }) => {
  const before = new Set(get().goals.map((g) => g.id));
  const stamp = Date.now();
  await get().addGoal(pick([
    ['title', title],
    ['description', description || ''],
    ['targetDate', target_date || ''],
    ['why', why],
    ['milestones', milestones.map((m, i) => ({ id: `m${stamp}_${i}`, title: m, completed: false }))],
    ['linkedHabitIds', linked_habit_ids],
  ]));
  await settle();
  const created = get().goals.find((g) => !before.has(g.id));
  return { result: { id: created?.id, title }, summary: `Added goal: ${title}` };
};

const updateGoal = async ({ get }, { goal_id, title, description, target_date, why, linked_habit_ids }) => {
  const goal = findOr(get().goals, goal_id, 'goal');
  if (target_date && !isDate(target_date)) fail('target_date must be YYYY-MM-DD or empty');
  await get().updateGoal(goal.id, pick([
    ['title', title], ['description', description], ['targetDate', target_date], ['why', why], ['linkedHabitIds', linked_habit_ids],
  ]));
  return { result: { updated: goal.id }, summary: `Updated goal: ${title || goal.title}` };
};

const setGoalDone = async ({ get }, { goal_id, done }) => {
  const goal = findOr(get().goals, goal_id, 'goal');
  if (!!goal.completed !== done) await get().toggleGoalComplete(goal.id);
  return { result: { goal: goal.title, done }, summary: `${goal.title}: ${done ? 'done' : 'reopened'}` };
};

const addMilestone = async ({ get }, { goal_id, title }) => {
  const goal = findOr(get().goals, goal_id, 'goal');
  await get().addMilestone(goal.id, title);
  return { result: { goal: goal.title, milestone: title }, summary: `Added milestone: ${title}` };
};

const findMilestone = (app, goal_id, milestone_id) => {
  const goal = findOr(app.goals, goal_id, 'goal');
  const milestone = (goal.milestones || []).find((m) => m.id === milestone_id) || fail(`No milestone ${milestone_id} on that goal.`);
  return { goal, milestone };
};

const setMilestoneDone = async ({ get }, { goal_id, milestone_id, done }) => {
  const { goal, milestone } = findMilestone(get(), goal_id, milestone_id);
  if (!!milestone.completed !== done) await get().toggleMilestone(goal.id, milestone.id);
  return { result: { milestone: milestone.title, done }, summary: `${milestone.title}: ${done ? 'done' : 'reopened'}` };
};

const deleteMilestone = async ({ get }, { goal_id, milestone_id }) => {
  const { goal, milestone } = findMilestone(get(), goal_id, milestone_id);
  await get().deleteMilestone(goal.id, milestone.id);
  return { result: { deleted: milestone.title }, summary: `Removed milestone: ${milestone.title}` };
};

const archiveGoal = async ({ get }, { goal_id }) => {
  const goal = findOr(get().goals, goal_id, 'goal');
  await get().archiveGoal(goal.id);
  return { result: { archived: goal.title }, summary: `Archived goal: ${goal.title}` };
};

const restoreGoal = async ({ get }, { goal_id, target_date }) => {
  const goal = findOr(get().archivedGoals, goal_id, 'archived goal');
  await get().restoreGoal(goal.id, target_date);
  return { result: { restored: goal.title }, summary: `Restored goal: ${goal.title}` };
};

const deleteGoal = async ({ get }, { goal_id }) => {
  const goal = findOr(get().goals, goal_id, 'goal');
  await get().deleteGoal(goal.id);
  return { result: { deleted: goal.title }, summary: `Deleted goal: ${goal.title}` };
};

// ---------- Calendar ----------

const addEvent = async ({ get }, { name, date, end_date, start_time, end_time, location, description, calendar_id }) => {
  const app = get();
  const calendarId = calendar_id || app.calendars[0]?.id || 'default';
  if (calendar_id) findOr(app.calendars, calendar_id, 'calendar');
  const event = await app.addEvent({
    name,
    date,
    startDate: date,
    endDate: end_date || date,
    startTime: start_time || null,
    endTime: end_time || null,
    location: location || '',
    description: description || '',
    calendarId,
  });
  return { result: { id: event?.id, name, date }, summary: `Added event: ${name}, ${date}${start_time ? ` ${start_time}` : ''}` };
};

const updateEvent = async ({ get }, { event_id, name, date, end_date, start_time, end_time, location, description }) => {
  const event = findOr(get().events, event_id, 'event');
  await get().updateEvent(event.id, pick([
    ['name', name],
    ['date', date],
    ['startDate', date],
    ['endDate', end_date || (date && (!event.endDate || event.endDate < date) ? date : undefined)],
    ['startTime', start_time],
    ['endTime', end_time],
    ['location', location],
    ['description', description],
  ]));
  return { result: { updated: event.id }, summary: `Updated event: ${name || event.name}` };
};

const deleteEvent = async ({ get }, { event_id }) => {
  const event = findOr(get().events, event_id, 'event');
  await get().deleteEvent(event.id);
  return { result: { deleted: event.name }, summary: `Deleted event: ${event.name}` };
};

// ---------- Focus timer, settings, profile ----------

const startFocus = async ({ get, pomodoro }, { task_id }) => {
  const timer = pomodoro();
  let label = 'Started focus timer';
  if (task_id) {
    const task = findOr(get().tasks, task_id, 'open task');
    timer.setTask(task.id, task.title);
    label = `Focusing on: ${task.title}`;
  }
  if (!timer.isRunning) timer.start();
  return { result: { running: true, time_left: timer.formattedTime }, summary: label };
};

const pauseFocus = async ({ pomodoro }) => {
  const timer = pomodoro();
  if (timer.isRunning) timer.pause();
  return { result: { running: false, time_left: timer.formattedTime }, summary: 'Paused focus timer' };
};

const setTheme = async ({ get, settle }, { mode }) => {
  if (mode === 'system' || mode === 'time') {
    await get().changeThemeMode(mode);
  } else {
    await get().changeThemeMode('manual');
    await settle();
    await settle();
    if (get().isDark !== (mode === 'dark')) await get().toggleTheme();
  }
  const names = { light: 'paper', dark: 'chalkboard', system: 'follow system', time: 'follow the sun' };
  return { result: { mode }, summary: `Theme: ${names[mode]}` };
};

const updateProfile = async ({ get }, { why, motivators, obstacles, tone, notes, assistant_name: name }) => {
  const updates = pick([
    ['why', why], ['motivators', motivators], ['obstacles', obstacles], ['tone', tone], ['notes', notes],
    ['assistantName', name === undefined ? undefined : cleanName(name)],
  ]);
  if (!Object.keys(updates).length) fail('Pass at least one field.');
  await get().updateUserData({ agentProfile: { ...(get().agentProfile || {}), ...updates } });
  return { result: { saved: Object.keys(updates) }, summary: 'Noted for next time' };
};

// ---------- Registry ----------

// The day for the assistant's suggestions (suggestions.js): the overview without IDs,
// finished tasks and far-off events, so it stays small and reads like a day
export const daySnapshot = (app) => {
  const full = overview({ get: () => app });
  const soon = shiftDate(full.today, 2);
  const noId = ({ id, calendar_id: calendarId, linked_goal_ids: goals, linked_habit_ids: habits, ...rest }) => rest;
  return {
    today: full.today,
    score: full.score,
    habits: full.habits.map(noId),
    open_tasks: full.tasks.filter((t) => !t.done).map(({ done, ...t }) => noId(t)),
    events_next_3_days: full.events.filter((e) => e.date <= soon).map(noId),
    goals: full.goals.filter((g) => !g.done).map(({ done, milestones, ...g }) => ({
      ...noId(g),
      milestones_done: `${milestones.filter((m) => m.done).length}/${milestones.length}`,
    })),
  };
};

const read = (fn) => async (ctx, input) => ({ result: await fn(ctx, input) });

export const TOOL_HANDLERS = {
  get_overview: read(overview),
  get_habit_history: read(habitHistory),
  list_archive: read(archive),
  add_habits: addHabits,
  update_habit: updateHabit,
  set_habit_done: setHabitDone,
  reorder_habits: reorderHabits,
  delete_habit: deleteHabit,
  add_tasks: addTasks,
  update_task: updateTask,
  set_task_done: setTaskDone,
  archive_task: archiveTask,
  restore_task: restoreTask,
  delete_task: deleteTask,
  add_goal: addGoal,
  update_goal: updateGoal,
  set_goal_done: setGoalDone,
  add_milestone: addMilestone,
  set_milestone_done: setMilestoneDone,
  delete_milestone: deleteMilestone,
  archive_goal: archiveGoal,
  restore_goal: restoreGoal,
  delete_goal: deleteGoal,
  add_event: addEvent,
  update_event: updateEvent,
  delete_event: deleteEvent,
  start_focus: startFocus,
  pause_focus: pauseFocus,
  set_theme: setTheme,
  update_profile: updateProfile,
};

// Tools whose effects the per-turn undo can't reverse
export const OUTSIDE_UNDO = new Set(['add_event', 'update_event', 'delete_event', 'start_focus', 'pause_focus', 'set_theme', 'update_profile']);

/**
 * What to ask before running a tool, or null to run it straight away.
 * Deletes always ask; calendar writes ask when they reach Google Calendar.
 */
export const confirmationFor = (name, input, app) => {
  const title = (list, id, key = 'title') => list.find((x) => x.id === id)?.[key] || 'this';
  switch (name) {
    case 'delete_habit':
      return { prompt: `Delete the habit "${title(app.habits, input.habit_id, 'name')}" and its history?`, action: 'Delete' };
    case 'delete_task':
      return { prompt: `Delete the task "${title(app.tasks, input.task_id)}"?`, action: 'Delete' };
    case 'delete_goal':
      return { prompt: `Delete the goal "${title(app.goals, input.goal_id)}"?`, action: 'Delete' };
    case 'delete_milestone': {
      const goal = app.goals.find((g) => g.id === input.goal_id);
      return { prompt: `Remove the milestone "${title(goal?.milestones || [], input.milestone_id)}"?`, action: 'Remove' };
    }
    case 'delete_event':
      return { prompt: `Delete the event "${title(app.events, input.event_id, 'name')}"?`, action: 'Delete' };
    case 'add_event': {
      const cal = calendarFor(app, input.calendar_id || app.calendars[0]?.id);
      return cal?.syncToGoogle ? { prompt: `Add "${input.name}" on ${input.date} to ${cal.name}? It will also appear in Google Calendar.`, action: 'Add', safe: true } : null;
    }
    case 'update_event': {
      const event = app.events.find((e) => e.id === input.event_id);
      const cal = event && calendarFor(app, event.calendarId);
      return cal?.syncToGoogle ? { prompt: `Change "${event.name}"? It will also change in Google Calendar.`, action: 'Change', safe: true } : null;
    }
    default:
      return null;
  }
};

/**
 * Run one tool call. Returns { content, isError, summary, group } for the
 * tool_result; `group` (optional) lets the log fold a run of calls into a line.
 */
export const runTool = async (name, input, ctx) => {
  const handler = TOOL_HANDLERS[name];
  if (!handler) return { content: `Unknown tool: ${name}`, isError: true };
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return { content: 'Tool input must be an object.', isError: true };
  }
  try {
    const { result, summary, group } = await handler(ctx, input);
    await ctx.settle();
    return { content: JSON.stringify(result ?? { ok: true }), isError: false, summary, group };
  } catch (error) {
    if (error instanceof ToolError) return { content: error.message, isError: true };
    console.error('[agent] Tool failed:', name, error);
    return { content: `That didn't work: ${error.message || 'unknown error'}`, isError: true };
  }
};
